<!-- Rendered in the app header's center slot (HeaderContentService) while it
     fits there on one line, shortened in stages first, else at the top of the
     page (menuStage, header-menu-fit.ts). Either way bindings resolve against
     this component, and the scoped styles apply because the template is
     declared here. -->
<ng-template #headerMenu>
    <div
        class="header-page-actions combined-orders-menu"
        [class]="menuStageClasses"
    >
        <!-- Buttons left, warnings center, counts right. -->
        <div class="actions-start">
            @if (canShip) {
                <dx-button
                    text="Schedule Pickup"
                    hint="Schedule courier pickup for selected orders"
                    icon="box"
                    type="default"
                    stylingMode="contained"
                    [disabled]="isDataLoading || selectedRowKeys.length === 0"
                    (onClick)="shipSelectedOrders('PICKUP')"
                ></dx-button>
                <dx-button
                    text="Drop Off"
                    hint="Arrange drop off for selected orders"
                    icon="export"
                    type="default"
                    stylingMode="outlined"
                    [disabled]="isDataLoading || selectedRowKeys.length === 0"
                    (onClick)="shipSelectedOrders('DROP_OFF')"
                ></dx-button>
            }
            @if (canPrint) {
                <dx-button
                    text="Print Both"
                    hint="Print invoices and shipping labels"
                    icon="print"
                    type="default"
                    stylingMode="outlined"
                    [disabled]="isDataLoading || selectedRowKeys.length === 0"
                    (onClick)="printBoth()"
                ></dx-button>
                <dx-button
                    text="Print Invoices"
                    hint="Print invoices only"
                    icon="print"
                    stylingMode="outlined"
                    [disabled]="isDataLoading || selectedRowKeys.length === 0"
                    (onClick)="printInvoices()"
                ></dx-button>
                <dx-button
                    text="Print Shipping Labels"
                    hint="Print shipping labels only"
                    icon="print"
                    stylingMode="outlined"
                    [disabled]="isDataLoading || selectedRowKeys.length === 0"
                    (onClick)="printShippingLabels()"
                ></dx-button>
            }
            <dx-button
                icon="refresh"
                hint="Refresh orders"
                [disabled]="isDataLoading"
                (onClick)="refreshOrders()"
            ></dx-button>
        </div>
        <div class="actions-center">
            @if (pendingReturnRequests.length > 0) {
                <dx-drop-down-button
                    class="return-request-notice"
                    icon="undo"
                    stylingMode="outlined"
                    text="คำขอคืนสินค้ารอตัดสินใจ ({{
                        pendingReturnRequests.length
                    }})"
                    [hint]="pendingReturnHint"
                    [items]="pendingReturnMenuItems"
                    displayExpr="text"
                    [dropDownOptions]="pendingReturnDropDownOptions"
                    (onItemClick)="onPendingReturnClick($event)"
                ></dx-drop-down-button>
            }
            @if (hasRemarks) {
                <div class="remark-notice" id="remarkNotice">
                    <i class="dx-icon-comment"></i>
                    <span>Some orders have remarks</span>
                </div>
            }
            @if (canShip && dropOffOnlyStoreTitles.length > 0) {
                <div class="drop-off-notice" id="dropOffNotice">
                    <i class="dx-icon-warning"></i>
                    <span
                        >Only Drop Off is supported for
                        {{ dropOffOnlyStoreTitles.join(", ") }}</span
                    >
                </div>
            }
        </div>
        <div class="actions-end">
            @if (
                (selectedRowKeys.length > 0 && !isDataLoading) ||
                receiptAvailableCount > 0 ||
                taxInvoiceRequestedCount > 0
            ) {
                <div class="count-bar">
                    @if (selectedRowKeys.length > 0 && !isDataLoading) {
                        <div
                            class="count-item selected-count"
                            title="{{ selectedRowKeys.length }} orders selected"
                        >
                            <i class="dx-icon-todo"></i>
                            <b>{{ selectedRowKeys.length }}</b>
                        </div>
                    }
                    @if (receiptAvailableCount > 0) {
                        <div
                            class="count-item receipt-count"
                            title="{{
                                receiptAvailableCount
                            }} orders with receipt info"
                        >
                            <i class="dx-icon-doc"></i>
                            <b>{{ receiptAvailableCount }}</b>
                        </div>
                    }
                    @if (taxInvoiceRequestedCount > 0) {
                        <div
                            class="count-item tax-invoice-count"
                            title="{{
                                taxInvoiceRequestedCount
                            }} orders requesting a tax invoice"
                        >
                            <i class="dx-icon-doc"></i>
                            <b>{{ taxInvoiceRequestedCount }}</b>
                        </div>
                    }
                </div>
            }
        </div>
    </div>
    <!-- Tooltips are placed after the badges so showing one doesn't push the
    next badge onto a new line. -->
    @if (hasRemarks) {
        <dx-tooltip
            target="#remarkNotice"
            showEvent="mouseenter"
            hideEvent="mouseleave"
            [hideOnOutsideClick]="false"
            [maxWidth]="360"
        >
            <div
                *dxTemplate="let tooltipData of 'content'"
                class="info-tooltip"
            >
                Hover the icon next to the Recipient Name to preview, click it
                to view and copy.
            </div>
        </dx-tooltip>
    }
    @if (canShip && dropOffOnlyStoreTitles.length > 0) {
        <dx-tooltip
            target="#dropOffNotice"
            showEvent="mouseenter"
            hideEvent="mouseleave"
            [hideOnOutsideClick]="false"
            [maxWidth]="360"
        >
            <div
                *dxTemplate="let tooltipData of 'content'"
                class="info-tooltip"
            >
                Schedule Pickup will arrange Drop Off for these orders.
            </div>
        </dx-tooltip>
    }
</ng-template>

<div class="dx-card padding-content">
    @if (menuStage() === inPageStage) {
        <div class="in-page-menu">
            <ng-container [ngTemplateOutlet]="headerMenu"></ng-container>
        </div>
    }

    <dx-tabs
        #statusTabs
        keyExpr="key"
        [items]="orderStatuses"
        [scrollByContent]="true"
        [showNavButtons]="true"
        (onSelectionChanged)="orderStatusSelectionChanged($event)"
        [(selectedItem)]="selectedTabItem"
    ></dx-tabs>

    <dx-data-grid
        #dataGrid
        id="combinedOrderGrid"
        height="100%"
        [showBorders]="true"
        [dataSource]="orderSource"
        [(selectedRowKeys)]="selectedRowKeys"
        [masterDetail]="{ enabled: true, template: 'combinedOrderDetails' }"
        [hoverStateEnabled]="true"
        [disabled]="isDataLoading"
        (onContentReady)="onGridContentReady()"
        (onContextMenuPreparing)="onContextMenuPreparing($event)"
        keyExpr="key"
    >
        <div
            *dxTemplate="let row of 'combinedOrderDetails'"
            class="order-detail"
        >
            <div class="detail-header">
                <span class="platform-badge platform-{{ row.data.platform }}">
                    {{ row.data.platform }}
                </span>
                <span class="detail-store">{{ row.data.storeTitle }}</span>
                <span class="detail-order-id">#{{ row.data.orderId }}</span>
                <span class="detail-matching-chip">{{
                    row.data.matching
                }}</span>
                <span class="detail-status">{{ row.data.status }}</span>
            </div>

            <div class="detail-sections">
                <div class="detail-section">
                    <div class="detail-section-title">Order</div>
                    <div class="detail-field">
                        <span class="detail-label">Created</span>
                        <span class="detail-value">
                            {{
                                (row.data.createDate
                                    | date: "d MMM y, HH:mm") || "—"
                            }}
                        </span>
                    </div>
                    <div class="detail-field">
                        <span class="detail-label">Items</span>
                        <span class="detail-value">{{
                            row.data.itemsCount
                        }}</span>
                    </div>
                    @if (row.data.lastPrintMoment) {
                        <div class="detail-field">
                            <span class="detail-label">Last Print</span>
                            <span class="detail-value">
                                {{
                                    row.data.lastPrintMoment
                                        | formatRelativeDatetime
                                }}
                            </span>
                        </div>
                    }
                </div>

                <div class="detail-section">
                    <div class="detail-section-title">Shipping</div>
                    <div class="detail-field">
                        <span class="detail-label">Recipient</span>
                        <span class="detail-value">
                            {{ row.data.recipientName || "—" }}
                        </span>
                    </div>
                    <div class="detail-field">
                        <span class="detail-label">Carrier</span>
                        <span class="detail-value">
                            {{ row.data.shippingCarrier || "—" }}
                        </span>
                    </div>
                    <div class="detail-field">
                        <span class="detail-label">Tracking No</span>
                        <span class="detail-value">
                            {{ row.data.trackingNumber || "—" }}
                        </span>
                    </div>
                </div>

                <div class="detail-section">
                    <div class="detail-section-title">Payment</div>
                    <div class="detail-field">
                        <span class="detail-label">Method</span>
                        <span class="detail-value">
                            {{ row.data.paymentMethod || "—" }}
                        </span>
                    </div>
                    <div class="detail-field">
                        <span class="detail-label">Total</span>
                        <span class="detail-value detail-total">
                            ฿ {{ row.data.totalAmount | formatNumber: 2 }}
                        </span>
                    </div>
                </div>
            </div>

            @if (row.data.remarks) {
                <div class="detail-remark">
                    <i class="dx-icon-comment"></i>
                    <span>{{ row.data.remarks }}</span>
                </div>
            }

            <dx-data-grid
                class="detail-items-grid"
                [showBorders]="true"
                [dataSource]="row.data.items"
                [rowAlternationEnabled]="true"
                [columnAutoWidth]="true"
            >
                <dxi-column
                    dataField="itemNo"
                    caption="#"
                    [width]="50"
                    dataType="number"
                    alignment="center"
                ></dxi-column>
                <dxi-column
                    dataField="description"
                    caption="Description"
                    [minWidth]="160"
                ></dxi-column>
                <dxi-column
                    dataField="quantity"
                    caption="Qty"
                    [width]="80"
                    dataType="number"
                ></dxi-column>
                <dxi-column
                    dataField="unitPrice"
                    caption="Unit Price"
                    [width]="110"
                    dataType="number"
                    cellTemplate="priceTemplate"
                ></dxi-column>
                <dxi-column
                    dataField="amount"
                    caption="Amount"
                    [width]="120"
                    dataType="number"
                    cellTemplate="priceTemplate"
                ></dxi-column>
                <div *dxTemplate="let cellData of 'priceTemplate'">
                    {{ cellData.value | formatNumber: 2 }}
                </div>
                <dxo-summary>
                    <dxi-total-item
                        column="quantity"
                        summaryType="sum"
                        displayFormat="{0} pcs"
                    >
                    </dxi-total-item>
                    <dxi-total-item
                        column="amount"
                        summaryType="sum"
                        valueFormat="฿ #,##0.00"
                        displayFormat="{0}"
                    >
                    </dxi-total-item>
                </dxo-summary>
            </dx-data-grid>
        </div>
        <!-- The grid's own panel would also flash on internal reloads (see
             dataGridBeginLoading); the standalone dx-load-panel below is the
             only loading indicator. -->
        <dxo-load-panel [enabled]="false"></dxo-load-panel>
        <dxo-filter-row [visible]="true"></dxo-filter-row>
        <dxo-selection
            selectAllMode="allPages"
            showCheckBoxesMode="onClick"
            mode="{{ selectionMode }}"
        ></dxo-selection>
        <dxo-paging [pageSize]="18"> </dxo-paging>
        <dxo-pager
            [visible]="true"
            [showPageSizeSelector]="false"
            [showInfo]="true"
            [showNavigationButtons]="true"
        >
        </dxo-pager>
        @if (showLastPrint) {
            <dxi-column
                dataField="lastPrintMoment"
                caption="Last Print"
                [width]="120"
                cellTemplate="relativeDatetimeCellTemplate"
            ></dxi-column>
        }
        <div *dxTemplate="let cellData of 'relativeDatetimeCellTemplate'">
            {{ cellData.value | formatRelativeDatetime }}
        </div>
        <dxi-column
            dataField="storeTitle"
            caption="Store"
            [width]="140"
        ></dxi-column>
        <dxi-column
            caption="Entity"
            [width]="110"
            [allowEditing]="false"
            [calculateCellValue]="entityIdFor"
        ></dxi-column>
        <dxi-column
            dataField="createDate"
            dataType="datetime"
            caption="Creation Time"
            [width]="150"
            format="d MMM y HH:mm"
            sortOrder="desc"
            alignment="left"
        >
        </dxi-column>
        <dxi-column
            dataField="orderId"
            caption="Order ID"
            [width]="170"
            alignment="left"
            cellTemplate="orderIdCellTemplate"
        ></dxi-column>
        <div
            *dxTemplate="let cellData of 'orderIdCellTemplate'"
            class="order-id-cell"
        >
            @if (cellData.data.receiptAvailable) {
                <i
                    class="dx-icon-doc receipt-available-icon"
                    [class.receipt-tax-invoice-icon]="
                        cellData.data.receiptDetail?.taxInvoiceRequested
                    "
                    id="receiptOk{{ cellData.rowIndex }}"
                    (click)="showReceiptPopup(cellData.data, $event)"
                ></i>
            } @else if (cellData.data.missingReceiptData) {
                <i
                    class="dx-icon-warning receipt-warning-icon"
                    id="receiptWarn{{ cellData.rowIndex }}"
                ></i>
            }
            {{ cellData.value }}
            @if (cellData.data.receiptAvailable) {
                <dx-tooltip
                    target="#receiptOk{{ cellData.rowIndex }}"
                    showEvent="mouseenter"
                    hideEvent="mouseleave"
                    [hideOnOutsideClick]="false"
                    [maxWidth]="360"
                >
                    <div *dxTemplate="let tooltipData of 'content'">
                        @if (cellData.data.receiptDetail) {
                            <app-receipt-detail
                                [detail]="cellData.data.receiptDetail"
                            ></app-receipt-detail>
                        } @else {
                            <div class="remark-tooltip">
                                Full receipt available
                            </div>
                        }
                    </div>
                </dx-tooltip>
            } @else if (cellData.data.missingReceiptData) {
                <dx-tooltip
                    target="#receiptWarn{{ cellData.rowIndex }}"
                    showEvent="mouseenter"
                    hideEvent="mouseleave"
                    [hideOnOutsideClick]="false"
                    [maxWidth]="360"
                >
                    <div
                        *dxTemplate="let tooltipData of 'content'"
                        class="info-tooltip"
                    >
                        No tax receipt or recognized customer info — the invoice
                        will use the shipping-label image.
                    </div>
                </dx-tooltip>
            }
        </div>
        <dxi-column
            dataField="matching"
            caption="Matching"
            [width]="90"
        ></dxi-column>
        <dxi-column
            dataField="recipientName"
            caption="Recipient Name"
            [minWidth]="140"
            cellTemplate="recipientNameCellTemplate"
        ></dxi-column>
        <div *dxTemplate="let cellData of 'recipientNameCellTemplate'">
            @if (cellData.data.remarks) {
                <i
                    class="dx-icon-comment remark-icon"
                    id="remarkIcon{{ cellData.rowIndex }}"
                    (click)="showRemarkPopup(cellData.data)"
                ></i>
            }
            {{ cellData.value }}
            @if (cellData.data.remarks) {
                <dx-tooltip
                    target="#remarkIcon{{ cellData.rowIndex }}"
                    showEvent="mouseenter"
                    hideEvent="mouseleave"
                    [hideOnOutsideClick]="false"
                    [maxWidth]="360"
                >
                    <div
                        *dxTemplate="let tooltipData of 'content'"
                        class="remark-tooltip"
                        [textContent]="cellData.data.remarks"
                    ></div>
                </dx-tooltip>
            }
        </div>
        <dxi-column
            dataField="paymentMethod"
            caption="Payment Method"
            [width]="130"
        ></dxi-column>
        <dxi-column
            dataField="totalAmount"
            caption="Total"
            [width]="100"
            dataType="number"
            cellTemplate="priceTemplate"
        ></dxi-column>
        <div *dxTemplate="let cellData of 'priceTemplate'">
            {{ cellData.value | formatNumber: 2 }}
        </div>
        <dxi-column
            dataField="itemsCount"
            caption="Items"
            [width]="80"
            dataType="number"
        ></dxi-column>
        @if (showCarrier) {
            <dxi-column
                dataField="shippingCarrier"
                caption="Carrier"
                [width]="130"
            ></dxi-column>
        }
        @if (showTracking) {
            <dxi-column
                dataField="trackingNumber"
                caption="Tracking No"
                [width]="140"
            ></dxi-column>
        }
        <dxo-summary>
            <dxi-total-item
                column="orderId"
                summaryType="count"
                valueFormat="#,##0"
                displayFormat="Total {0} order(s)"
            >
            </dxi-total-item>
            <dxi-total-item
                column="totalAmount"
                summaryType="sum"
                valueFormat="฿ #,##0.00"
                displayFormat="{0}"
            >
            </dxi-total-item>
            <dxi-total-item
                column="itemsCount"
                summaryType="sum"
                valueFormat="#,##0"
                displayFormat="{0} item(s)"
            >
            </dxi-total-item>
        </dxo-summary>
    </dx-data-grid>

    <dx-popup
        [(visible)]="detailPopupVisible"
        [width]="420"
        height="auto"
        [showCloseButton]="true"
        [title]="detailPopupTitle"
    >
        <div *dxTemplate="let popupData of 'content'">
            @if (detailPopupReceipt) {
                <app-receipt-detail
                    [detail]="detailPopupReceipt"
                ></app-receipt-detail>
            } @else {
                <div class="remark-popup-text">{{ detailPopupText }}</div>
            }
        </div>
        <dxi-toolbar-item
            widget="dxButton"
            toolbar="bottom"
            location="after"
            [options]="copyDetailButtonOptions"
        ></dxi-toolbar-item>
    </dx-popup>

    <app-receipt-info-popup
        #receiptInfoPopup
        (saved)="onReceiptInfoSaved($event)"
    ></app-receipt-info-popup>

    <dx-load-panel
        [visible]="isDataLoading"
        [position]="loadPanelPosition"
        [showIndicator]="true"
        [showPane]="true"
        [shading]="false"
        message=""
    ></dx-load-panel>
</div>
